/**
 * Template utility functions for the Player carousel.
 *
 * These helpers centralise the "how many items does this template consume?"
 * logic that was previously duplicated in three places inside the Player
 * component (calculateTotalSlides, the auto-advance timer, getCurrentTemplate,
 * and renderData).
 */

/**
 * Return how many source items a single slide consumes for a given template.
 *
 * Templates that duplicate a single image ("n2Up", "4up", etc.) advance by 1.
 * Templates that pull consecutive images advance by 2 or 4.
 */
export const getAdvanceBy = (template) => {
  switch (template) {
    case "1up":
    case "n2Up":
    case "n2Up-mirror-left":
    case "n2Up-mirror-right":
    case "4up":
    case "4up-warhol":
    case "grid-5x4-center":
      return 1;
    case "2next":
      return 2;
    case "4next":
      return 4;
    default:
      return 1;
  }
};

/**
 * Walk through `items` and count how many discrete slides they produce.
 */
export const calculateTotalSlides = (items) => {
  let slides = 0;
  let idx = 0;

  while (idx < items.length) {
    const template = items[idx]?.template || "1up";
    idx += getAdvanceBy(template);
    slides++;
  }

  return slides;
};

/**
 * Given a slide index, return the item-array index of the first item that
 * belongs to that slide.
 */
export const getItemIndexForSlide = (items, slideIndex) => {
  let idx = 0;

  for (let i = 0; i < slideIndex && idx < items.length; i++) {
    const item = items[idx];
    if (!item) break;
    const template = item?.template || "1up";
    idx += getAdvanceBy(template);
  }

  return idx;
};

/**
 * Return the template string for the slide at `slideIndex`.
 */
export const getCurrentTemplate = (items, slideIndex) => {
  if (items.length === 0) {
    return "1up";
  }

  const idx = getItemIndexForSlide(items, slideIndex);

  if (idx < items.length) {
    return items[idx]?.template || "1up";
  }

  return "1up";
};
